import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { StarRating } from "../ratingComponents";
import styles from "./Review.scss";
import classnamesBind from "classnames/bind";
import ExpandableText from "../expandableText/ExpandableText";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useReviewTranslation } from "../../context/reviewTranslation/useReviewTranslation";
import ReportReviewButton from "../reportReviewButton/ReportReviewButton";
import StandardSpinner from "../spinner/standard/StandardSpinner";
import {
  SYNDICATED_REVIEW,
  STAFF_REVIEW,
  VERIFIED_REVIEW,
  INCENTIVIZED_REVIEW,
  INLINE_SPINNER,
  FR_STORE,
} from "../../constants";
import IncentivisedBadge from "../incentivisedBadge/IncentivisedBadge";
import { getReviewTranslation } from "../../api/reviewTranslationApi";
import { fireReviewTranslationClicked } from "../../utils/marketingTracking/marketingTrackingEvents";

const classnames = classnamesBind.bind(styles);
const MAX_INLINE_THUMBNAILS = 3;

const Review = ({
  title,
  reviewText,
  translatedTitle,
  translatedReviewText,
  contentLocale,
  translationEnabled,
  rating,
  submissionRecency,
  syndicationSource,
  badgesOrder,
  photos,
  additionalClasses,
  displayPhotos,
  togglePhotoGallery,
  onExpandCallback,
  reviewIndex = 1,
  id,
  badges,
  onReportSuccess,
  isReported,
  reviewIsInModal,
  dateOfUserExperience,
  dateOfUserExperienceLabel,
}) => {
  const {
    getTranslation,
    webContext,
    product,
    newLocaleMappingStores = [],
  } = useRuntimeConstants();
  const {
    showTranslatedReview,
    translation,
    setTranslation,
    toggleShowTranslated,
  } = useReviewTranslation(id);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);
  const hasFiredTranslationClickRef = useRef(false);
  const reviewSource = getReviewSource(syndicationSource, badgesOrder, badges);
  const formattedDateOfUserExperience =
    webContext?.storeId === FR_STORE && dateOfUserExperienceLabel
      ? formatDateOfUserExperience(dateOfUserExperience, "fr-FR")
      : null;
  const sourceLanguageCode = getLanguageCode(contentLocale);
  const targetLanguageCode = getLanguageCode(webContext?.browseLanguage);
  const sourceLanguage = getLanguageName(
    contentLocale,
    webContext?.browseLanguage,
  );
  const targetLanguage = getLanguageName(webContext?.browseLanguage);
  const isTranslationEnabledForReview =
    newLocaleMappingStores.includes(webContext?.storeId) &&
    translationEnabled === true &&
    !!sourceLanguageCode &&
    !!targetLanguageCode &&
    sourceLanguageCode !== targetLanguageCode;
  const shouldShowTranslationCta = shouldDisplayTranslationCta({
    isTranslationEnabledForReview,
    targetLanguage,
    title,
    reviewText,
  });
  const isShowingTranslation =
    isTranslationEnabledForReview && showTranslatedReview;
  const displayTitle =
    (isShowingTranslation && (translation?.title || translatedTitle)) || title;
  const displayReviewText =
    (isShowingTranslation && (translation?.review || translatedReviewText)) ||
    reviewText;

  const handleTranslateClick = async () => {
    if (translationError) {
      setTranslationError(false);
    }

    if (showTranslatedReview) {
      toggleShowTranslated(false);
      return;
    }

    if (!hasFiredTranslationClickRef.current) {
      hasFiredTranslationClickRef.current = true;
      fireReviewTranslationClicked({
        product,
        reviewId: id,
        sourceLanguage: sourceLanguageCode,
        targetLanguage: targetLanguageCode,
      });
    }

    if (translation || translatedTitle || translatedReviewText) {
      toggleShowTranslated(true);
      return;
    }

    setIsTranslating(true);
    setTranslationError(false);

    const result = await getReviewTranslation({
      productId: product?.id,
      reviewId: id,
      store: webContext.storeId,
    });

    if (hasTranslatedContent(result?.translation)) {
      setTranslation(result.translation, true);
    } else {
      setTranslationError(true);
    }

    setIsTranslating(false);
  };

  useEffect(() => {
    if (badges?.incentivizedReview) {
      requestAnimationFrame(() => {
        onExpandCallback?.();
      });
    }
  }, [badges?.incentivizedReview, onExpandCallback]);

  useEffect(() => {
    if (contentRef.current && !isTranslating) {
      const fullHeight = contentRef.current.offsetHeight;
      const height = fullHeight - 32;
      setContentHeight(height);
    }
  }, [displayTitle, displayReviewText, isTranslating]);

  const ratingsAriaLabel = getRatingAriaLabel(
    getTranslation,
    {
      submissionRecency,
      syndicationSource,
      rating,
      reviewSource,
    },
    reviewIndex,
  );
  const ratingsText = getSourceText(getTranslation, {
    syndicationSource,
    rating,
    reviewSource,
  });

  return (
    <section
      className={additionalClasses}
      aria-label={ratingsAriaLabel}
      role={"group"}
      tabIndex={-1}
      data-testid="reviewsModalReview"
    >
      <div className={styles.rating}>
        <div className={styles.starsAndRecencyContainer}>
          <StarRating rating={rating} />
          <span className={styles.recency}>{submissionRecency}</span>
        </div>
        <p className={styles.source}>{ratingsText}</p>
      </div>
      {shouldShowTranslationCta && (
        <div className={styles.translationActionContainer}>
          <button
            className={styles.translateButton}
            type="button"
            disabled={isTranslating}
            onClick={handleTranslateClick}
            aria-busy={isTranslating}
            aria-pressed={showTranslatedReview}
            aria-describedby={
              showTranslatedReview ? `translation-label-${id}` : undefined
            }
            aria-label={getTranslation(
              showTranslatedReview
                ? "pdp_ratings_reviews_reviews_show_original_button"
                : "pdp_ratings_reviews_reviews_translate_button",
              showTranslatedReview ? {} : { language: targetLanguage },
            )}
            data-testid="reviewTranslateButton"
          >
            {isTranslating
              ? getTranslation("pdp_ratings_reviews_translation_loading")
              : translationError
                ? getTranslation("pdp_ratings_reviews_translation_error")
                : showTranslatedReview
                  ? getTranslation(
                      "pdp_ratings_reviews_reviews_show_original_button",
                    )
                  : getTranslation(
                      "pdp_ratings_reviews_reviews_translate_button",
                      {
                        language: targetLanguage,
                      },
                    )}
          </button>
          {isTranslating && (
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className={styles.screenReaderOnly}
            >
              {getTranslation("pdp_ratings_reviews_translation_loading")}
            </div>
          )}
          {translationError && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={styles.screenReaderOnly}
            >
              {getTranslation("pdp_ratings_reviews_translation_error")}
            </div>
          )}
        </div>
      )}
      {(displayTitle || displayReviewText || isTranslating) && (
        <div
          className={classnames({
            translationReviewContent: shouldShowTranslationCta,
          })}
          ref={contentRef}
        >
          {isTranslating ? (
            <div
              className={styles.translatingContainer}
              style={{
                minHeight: contentHeight ? `${contentHeight}px` : "auto",
              }}
            >
              <StandardSpinner size={INLINE_SPINNER} />
            </div>
          ) : (
            <div>
              {displayTitle && (
                <h4
                  className={classnames(styles.title, {
                    headerSpace: !shouldShowTranslationCta,
                    contentSpace: !!displayReviewText,
                  })}
                >
                  {displayTitle}
                </h4>
              )}
              {displayReviewText && (
                <ExpandableText
                  hasTitle={!!displayTitle}
                  text={displayReviewText}
                  readMoreTranslationKey="rdp_ratings_reviews_read_more"
                  testId="reviewsReadMore"
                  onExpandCallback={onExpandCallback}
                  reviewIsInModal={reviewIsInModal}
                  suppressHeaderSpace={shouldShowTranslationCta}
                />
              )}
              {isTranslationEnabledForReview && showTranslatedReview && (
                <p
                  className={styles.translatedFlag}
                  id={`translation-label-${id}`}
                  role="status"
                  aria-live="polite"
                >
                  <span
                    aria-hidden="true"
                    className={classnames(
                      styles.translatedFlagIcon,
                      "product-globe",
                    )}
                  />
                  {getTranslation(
                    "pdp_ratings_reviews_reviews_translated_flag",
                    {
                      language: sourceLanguage,
                    },
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {formattedDateOfUserExperience && (
        <p
          className={styles.dateOfUserExperience}
          data-testid="dateOfUserExperience"
        >
          {dateOfUserExperienceLabel}{" "}
          <time dateTime={dateOfUserExperience}>
            {formattedDateOfUserExperience}
          </time>
        </p>
      )}
      {shouldDisplayPhotos({ photos, displayPhotos, togglePhotoGallery }) && (
        <div
          className={classnames(styles.thumbnails, {
            [styles.thumbnailsGrid]: !reviewIsInModal,
            [styles.thumbnailsModal]: reviewIsInModal,
            [styles.thumbnailsModalCentered]:
              reviewIsInModal && photos.length >= MAX_INLINE_THUMBNAILS,
          })}
        >
          {photos
            .slice(0, MAX_INLINE_THUMBNAILS)
            .map(({ thumbnailUrl, fullSizeUrl }, index) => {
              const imageUrl = fullSizeUrl || thumbnailUrl;
              const hiddenPhotoCount =
                index === MAX_INLINE_THUMBNAILS - 1 ? photos.length - index : 0;
              const showMoreCount = hiddenPhotoCount > 1;

              return (
                <button
                  data-testid="reviewsModalThumbnailButton"
                  className={styles.thumbnail}
                  key={thumbnailUrl}
                  aria-label={`${getTranslation(
                    "pdp_ratings_review_gallery_image_count",
                    { index: index + 1, total: photos.length },
                  )}. ${getTranslation("pdp_media_gallery_thumbnail_instruction")}`}
                  onClick={(event) =>
                    togglePhotoGallery(true, photos, index, event.target)
                  }
                >
                  <img
                    className={styles.thumbnailImage}
                    src={imageUrl}
                    loading="lazy"
                    alt={getTranslation(
                      "rdp_ratings_reviews_accessibility_image_description",
                    )}
                  />
                  {showMoreCount && (
                    <span
                      className={styles.thumbnailMoreCount}
                      data-testid="reviewsThumbnailMoreCount"
                      aria-hidden="true"
                    >
                      +{hiddenPhotoCount}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      )}
      <div
        className={styles.reviewBottomSectionContainer}
        data-testid="reviewActions"
      >
        {badges?.incentivizedReview && <IncentivisedBadge />}
        <ReportReviewButton
          reviewId={id}
          onReportSuccess={onReportSuccess}
          isReported={isReported}
          reviewIsInModal={reviewIsInModal}
        />
      </div>
    </section>
  );
};

Review.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  title: PropTypes.string,
  reviewText: PropTypes.string,
  translatedTitle: PropTypes.string,
  translatedReviewText: PropTypes.string,
  contentLocale: PropTypes.string,
  translationEnabled: PropTypes.bool,
  rating: PropTypes.number.isRequired,
  isReported: PropTypes.bool.isRequired,
  submissionRecency: PropTypes.string.isRequired,
  syndicationSource: PropTypes.shape({ name: PropTypes.string.isRequired }),
  badges: PropTypes.shape({
    incentivizedReview: PropTypes.shape({
      contentType: PropTypes.string,
      id: PropTypes.string,
      badgeType: PropTypes.string,
    }),
  }),
  badgesOrder: PropTypes.array.isRequired,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      thumbnailUrl: PropTypes.string.isRequired,
      fullSizeUrl: PropTypes.string,
    }),
  ).isRequired,
  additionalClasses: PropTypes.string,
  displayPhotos: PropTypes.bool,
  togglePhotoGallery: PropTypes.func,
  onExpandCallback: PropTypes.func,
  onReportSuccess: PropTypes.func,
  reviewIndex: PropTypes.number,
  reviewIsInModal: PropTypes.bool,
  dateOfUserExperience: PropTypes.string,
  dateOfUserExperienceLabel: PropTypes.string,
};

const formatDateOfUserExperience = (date, language = "en-GB") => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;

  const [, year, month, day] = match;
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  if (
    parsedDate.getUTCFullYear() !== Number(year) ||
    parsedDate.getUTCMonth() !== Number(month) - 1 ||
    parsedDate.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
};

const shouldDisplayPhotos = ({ photos, displayPhotos, togglePhotoGallery }) =>
  !!(displayPhotos && togglePhotoGallery && photos.length);

const shouldDisplayTranslationCta = ({
  isTranslationEnabledForReview,
  targetLanguage,
  title,
  reviewText,
}) =>
  isTranslationEnabledForReview && !!targetLanguage && !!(title || reviewText);

const hasTranslatedContent = (translation) =>
  [translation?.title, translation?.review].some(
    (content) => typeof content === "string" && content.trim().length > 0,
  );

const getLanguageName = (contentLocale, displayLocale = contentLocale) => {
  const languageCode = getLanguageCode(contentLocale);
  const displayLanguage = displayLocale?.replace("_", "-") || "en";

  if (!languageCode) {
    return contentLocale;
  }

  try {
    return new Intl.DisplayNames([displayLanguage], { type: "language" }).of(
      languageCode,
    );
  } catch {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(languageCode);
  }
};

const getLanguageCode = (locale) =>
  locale?.replace("-", "_").split("_")[0].toLowerCase();

const getReviewSource = (syndicationSource, badgesOrder, badges) => {
  if (syndicationSource) {
    return SYNDICATED_REVIEW;
  }
  if (badgesOrder.length) {
    if (badgesOrder.includes("Staff")) {
      return STAFF_REVIEW;
    }
    return VERIFIED_REVIEW;
  }
  if (badges?.incentivizedReview) {
    return INCENTIVIZED_REVIEW;
  }
};

const getSourceText = (
  translation,
  { syndicationSource = null, reviewSource = null },
) => {
  return {
    [SYNDICATED_REVIEW]: translation(
      "pdp_ratings_reviews_syndication_badge_text",
      {
        syndicationSourceName: syndicationSource?.name,
      },
    ),
    [STAFF_REVIEW]: translation(
      "pdp_ratings_reviews_verified_staff_purchase_badge_text",
    ),
    [VERIFIED_REVIEW]: translation(
      "pdp_ratings_reviews_verified_purchaser_badge_text",
    ),
  }[reviewSource];
};

const getRatingAriaLabel = (
  translation,
  { submissionRecency, syndicationSource, rating, reviewSource },
  reviewIndex,
) => {
  if (reviewSource === SYNDICATED_REVIEW) {
    return `${translation("pdp_ratings_reviews_accessibility_identifier", {
      smart_count: reviewIndex,
      count: reviewIndex,
    })}. ${translation(
      "pdp_ratings_reviews_accessibility_individual_review_intro_syndicated",
      {
        submissionRecency,
        syndicationSourceName: syndicationSource?.name ?? "",
        rating,
      },
    )}`;
  }

  return `${translation("pdp_ratings_reviews_accessibility_identifier", {
    smart_count: reviewIndex,
    count: reviewIndex,
  })}. ${translation(
    "pdp_ratings_reviews_accessibility_individual_review_intro_verified_purchaser",
    {
      submissionRecency,
      badgeName: getSourceText(translation, {
        syndicationSource,
        reviewSource,
      }),
      rating,
    },
  )}`;
};

export default Review;
